import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import api from "../api/client";
import { AuthContext } from "../context/AuthContext";

/**
 * UserModalAlert - Displays persistent modals targeted at specific users.
 * These modals show on every page change/refresh until deactivated by an admin.
 */
export default function UserModalAlert() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [modals, setModals] = useState([]);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchActiveModals = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get("/usermodals/active");
      const data = response.data || [];
      
      if (data.length > 0) {
        setModals(data);
        setCurrentModalIndex(0);
        setOpen(true);
      } else {
        setModals([]);
        setOpen(false);
      }
    } catch (error) {
      // Silent fail - don't disrupt user experience
      console.error("Failed to fetch user modals:", error);
    }
  }, [user]);

  // Fetch modals on location change (page navigation)
  useEffect(() => {
    // Skip on login page
    if (location.pathname === "/login" || location.pathname === "/apply") {
      return;
    }
    
    // Small delay to let the page render first
    const timer = setTimeout(fetchActiveModals, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, fetchActiveModals]);

  const handleAcknowledge = () => {
    // Move to next modal if there are more
    if (currentModalIndex < modals.length - 1) {
      setCurrentModalIndex(currentModalIndex + 1);
    } else {
      // All modals acknowledged for this page view
      setOpen(false);
    }
  };

  if (!user || modals.length === 0 || !open) return null;

  const currentModal = modals[currentModalIndex];

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)"
              : "linear-gradient(135deg, #c62828 0%, #ef5350 100%)",
          color: "white",
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {currentModal.title}
          </Typography>
        </Box>
      </Box>

      <DialogTitle sx={{ pb: 1, pt: 2 }}>
        {modals.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Alert {currentModalIndex + 1} of {modals.length}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.7,
          }}
        >
          {currentModal.message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={handleAcknowledge}
          fullWidth
          sx={{ py: 1 }}
        >
          {currentModalIndex < modals.length - 1 ? "Next Alert" : "I Understand"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
